// /workspace-setup — pending-activation polling page.
//
// Hits GET /api/tenant every 3 seconds until status flips to `active`,
// then redirects to the dashboard. Surfaces a "taking longer than
// expected" hint after 30s, gives up after 5 minutes.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { tenantClient } from "@/api/tenantClient";

const POLL_MS = 3_000;
const SLOW_AFTER_MS = 30_000;

export function WorkspaceSetupPage(): JSX.Element {
  const [slow, setSlow] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const startedAt = Date.now();
    const slowTimer = window.setTimeout(() => {
      if (!cancelled) setSlow(true);
    }, SLOW_AFTER_MS);

    async function tick(): Promise<void> {
      try {
        const t = await tenantClient.get();
        if (cancelled) return;
        if (t.status === "active") {
          navigate("/", { replace: true });
          return;
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Polling failed");
      }
      if (cancelled) return;
      const elapsed = Date.now() - startedAt;
      // After ~5 minutes, stop polling to avoid infinite loops.
      if (elapsed > 5 * 60 * 1000) {
        setError(
          "Workspace activation is taking longer than expected. Please reach out to support.",
        );
        return;
      }
      window.setTimeout(tick, POLL_MS);
    }

    void tick();

    return () => {
      cancelled = true;
      window.clearTimeout(slowTimer);
    };
  }, [navigate]);

  return (
    <div className="reb-page">
      <div className="reb-card" style={{ textAlign: "center" }}>
        <h1 className="reb-page__title">Finalizing your workspace...</h1>
        <p className="reb-page__subtitle">
          Hang tight while we finish the link with Ring. This usually takes only
          a few seconds.
        </p>
        {slow && (
          <p className="reb-error" role="status">
            Taking longer than expected. We're still trying.
          </p>
        )}
        {error && (
          <p className="reb-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
